import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { RegistrationProvider } from './context/RegistrationContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Public Pages
import Home from './pages/public/Home';
import GurukulList from './pages/public/GurukulList';
import GurukulProfile from './pages/public/GurukulProfile';
import Donations from './pages/public/Donations';
import PaymentCallback from './pages/public/PaymentCallback';
import CheckKycStatus from './pages/public/CheckKycStatus';
import Security from './pages/public/Security';
import Feedback from './pages/public/Feedback';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsAndConditions from './pages/public/TermsAndConditions';
import AboutUs from './pages/public/AboutUs';
import VoucherUploadFromOtherDevice from './pages/public/VoucherUploadFromOtherDevice';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// User Pages
import Profile from './pages/user/Profile';
import MyDonations from './pages/user/MyDonations';
import MyReports from './pages/user/MyReports';
import MyFeedback from './pages/user/MyFeedback';
import Reports from './pages/public/Reports';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import GurukulManagement from './pages/admin/GurukulManagement';
import AdminManagement from './pages/admin/AdminManagement';
import GurukulFormPage from './pages/admin/GurukulFormPage';
import DocumentUpload from './pages/admin/DocumentUpload';
import FAQManagement from './pages/admin/FAQManagement';
import ManageVouchers from './pages/admin/ManageVouchers';
import PageViewCounters from './pages/admin/PageViewCounters';
import BannerManagement from './pages/admin/BannerManagement';
import BankDetailsManagement from './pages/admin/BankDetailsManagement';
import FeedbackManagement from './pages/admin/FeedbackManagement';

function App() {
  return (
    <Router>
      <AuthProvider>
        <RegistrationProvider>
          <div className="min-h-screen bg-primary-50 flex flex-col">
            <Header />
            <main className="flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/gurukuls" element={<GurukulList />} />
                <Route path="/gurukuls/:linkOrId" element={<GurukulProfile />} />
                <Route
                  path="/donations"
                  element={
                    <ProtectedRoute redirectMessageKey="auth.loginToViewDonations">
                      <Donations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route path="/payment/verify" element={<PaymentCallback />} />
                <Route path="/public/voucher-upload/:token" element={<VoucherUploadFromOtherDevice />} />
                <Route path="/check-kyc-status" element={<CheckKycStatus />} />
                <Route path="/security" element={<Security />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/about" element={<AboutUs />} />

                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/register-gurukul" element={<GurukulFormPage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protected User Routes */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-donations"
                  element={
                    <ProtectedRoute>
                      <MyDonations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-reports"
                  element={
                    <ProtectedRoute>
                      <MyReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-feedback"
                  element={
                    <ProtectedRoute>
                      <MyFeedback />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Candidate Routes */}
                <Route
                  path="/gurukul/my-profile"
                  element={
                    <ProtectedRoute candidateOnly>
                      <GurukulFormPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute adminOnly>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gurukuls"
                  element={
                    <ProtectedRoute adminOnly>
                      <GurukulManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gurukuls/new"
                  element={
                    <ProtectedRoute adminOnly>
                      <GurukulFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/gurukuls/:id/edit"
                  element={
                    <ProtectedRoute adminOnly>
                      <GurukulFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/donations"
                  element={
                    <ProtectedRoute adminOnly>
                      <Donations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/admins"
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/documents"
                  element={
                    <ProtectedRoute adminOnly>
                      <DocumentUpload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/faqs"
                  element={
                    <ProtectedRoute adminOnly>
                      <FAQManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/vouchers"
                  element={
                    <ProtectedRoute adminOnly>
                      <ManageVouchers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/feedback"
                  element={
                    <ProtectedRoute adminOnly>
                      <FeedbackManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/counters"
                  element={
                    <ProtectedRoute adminOnly>
                      <PageViewCounters />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/banner"
                  element={
                    <ProtectedRoute adminOnly>
                      <BannerManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/bank-details"
                  element={
                    <ProtectedRoute adminOnly>
                      <BankDetailsManagement />
                    </ProtectedRoute>
                  }
                />

                {/* 404 Route */}
                <Route
                  path="*"
                  element={
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                        <p className="text-gray-600">Page not found</p>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </main>
            <Footer />
          </div>
        </RegistrationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
